import { useEffect, useState, useCallback, useRef } from 'react';
import { useWebSocket } from './useWebSocket';
import { Entity, Position, ServerMessage } from '../types/simulation';

export interface EntityInterpolationData {
  targetPosition: Position;
  lastUpdateTime: number;
}

export function useSimulation(serverUrl: string) {
  const { connect, disconnect: wsDisconnect, send, sendBinary, status, lastMessage, error } = useWebSocket(serverUrl);

  const [entities, setEntities] = useState<Entity[]>([]);
  const [myEntityId, setMyEntityId] = useState<string | null>(null);
  const [receivedAudio, setReceivedAudio] = useState<number[]>(new Array(2048).fill(0)); // 2048 PCM samples
  const [currentPosition, setCurrentPosition] = useState<number[]>([0, 0, 0]);

  const pendingJoinRef = useRef<{ entityId: string; position: number[]; token: string; simulationId: string } | null>(null);
  const interpolationDataRef = useRef<Map<string, EntityInterpolationData>>(new Map());
  const hasJoinedRef = useRef(false);

  // Auto-connect on mount
  useEffect(() => {
    connect();
  }, [connect]);

  // Send pending join message when connected
  useEffect(() => {
    if (status === 'connected' && pendingJoinRef.current && !hasJoinedRef.current) {
      const { entityId, position, token, simulationId } = pendingJoinRef.current;
      pendingJoinRef.current = null;
      hasJoinedRef.current = true;
      setCurrentPosition(position);
      send({
        type: 'join',
        entity_id: entityId,
        position,
        token,
        simulation_id: simulationId,
      });
    }
  }, [status, send]);

  // Convert array position to Position object
  useCallback((arr: number[]): Position => ({
    x: arr[0] || 0,
    y: arr[1] || 0,
    z: arr[2] || 0,
  }), []);
// Handle incoming server messages
  useEffect(() => {
    if (!lastMessage) return;

    const message = lastMessage as ServerMessage;

    const arrayToPos = (arr: number[]): Position => ({
      x: arr[0] || 0,
      y: arr[1] || 0,
      z: arr[2] || 0,
    });

    switch (message.type) {
      case 'joined':
        setMyEntityId(message.entity_id);
        console.log(`Joined as ${message.entity_id}`);
        // Request entity list after joining
        send({ type: 'list_entities' });
        break;

      case 'entity_list':
        const now = Date.now();
        message.entities.forEach((e) => {
          const pos = arrayToPos(e.position);
          // Initialize interpolation data for other entities
          if (e.id !== myEntityIdRef.current) {
            interpolationDataRef.current.set(e.id, {
              targetPosition: pos,
              lastUpdateTime: now,
            });
          }
        });
        setEntities(
          message.entities.map((e) => ({
            id: e.id,
            position: arrayToPos(e.position),
          }))
        );
        break;

      case 'entity_joined':
        const joinPos = arrayToPos(message.position);
        const joinTime = Date.now();
        if (message.entity_id !== myEntityIdRef.current) {
          interpolationDataRef.current.set(message.entity_id, {
            targetPosition: joinPos,
            lastUpdateTime: joinTime,
          });
        }
        setEntities((prev) => [
          ...prev,
          {
            id: message.entity_id,
            position: joinPos,
          },
        ]);
        console.log(`Entity joined: ${message.entity_id}`);
        break;

      case 'entity_left':
        interpolationDataRef.current.delete(message.entity_id);
        setEntities((prev) => prev.filter((e) => e.id !== message.entity_id));
        console.log(`Entity left: ${message.entity_id}`);
        break;

      case 'position_update':
        // Server sent updated position for an entity
        const updatePos = arrayToPos(message.position);
        const updateTime = Date.now();

        // If this is our own entity, perform server reconciliation
        if (message.entity_id === myEntityIdRef.current) {
          // Check if our predicted position differs significantly from server
          const serverPos = message.position;
          const currentPos = currentPositionRef.current;
          const diff = Math.sqrt(
            Math.pow(currentPos[0] - serverPos[0], 2) +
            Math.pow(currentPos[1] - serverPos[1], 2) +
            Math.pow(currentPos[2] - serverPos[2], 2)
          );

          // If difference is significant (>0.5 units), correct to server position
          if (diff > 0.5) {
            console.log(`Server correction: diff=${diff.toFixed(2)}`);
            setCurrentPosition(serverPos);
            setEntities((prev) =>
              prev.map((e) =>
                e.id === myEntityIdRef.current
                  ? { ...e, position: updatePos }
                  : e
              )
            );
          }
        } else {
          // For other entities, update target position for interpolation
          const interpData = interpolationDataRef.current.get(message.entity_id);
          if (interpData) {
            interpData.targetPosition = updatePos;
            interpData.lastUpdateTime = updateTime;
          }
          // Also update the entities state so the entity exists in the list
          setEntities((prev) =>
            prev.map((e) =>
              e.id === message.entity_id
                ? { ...e, position: updatePos }
                : e
            )
          );
        }
        break;

      case 'audio_input':
        setReceivedAudio(message.samples);
        break;

      case 'error':
        console.error('Server error:', message.message);
        break;
    }
  }, [lastMessage, send]);

  // Join the simulation
  const join = useCallback(
    (entityId: string, position: number[], token: string, simulationId: string) => {
      // Prevent duplicate joins
      if (hasJoinedRef.current) {
        return;
      }

      setCurrentPosition(position);

      if (status === 'connected') {
        // Send immediately if connected
        hasJoinedRef.current = true;
        send({
          type: 'join',
          entity_id: entityId,
          position,
          token,
          simulation_id: simulationId,
        });
      } else {
        // Clear any old pending join and store new parameters
        pendingJoinRef.current = { entityId, position, token, simulationId };
        connect();
      }
    },
    [send, connect, status]
  );

  // Store myEntityId in ref to avoid updatePosition recreating
  const myEntityIdRef = useRef(myEntityId);
  useEffect(() => {
    myEntityIdRef.current = myEntityId;
  }, [myEntityId]);

  // Update position (client-side prediction)
  const updatePosition = useCallback(
    (position: number[]) => {
      setCurrentPosition(position);

      // Client-side prediction: update local entity immediately
      setEntities((prev) =>
        prev.map((e) =>
          e.id === myEntityIdRef.current
            ? { ...e, position: { x: position[0] || 0, y: position[1] || 0, z: position[2] || 0 } }
            : e
        )
      );
    },
    []
  );

  // Throttled position updates to server (10 updates/sec)
  const lastSentPositionRef = useRef<number[]>([0, 0, 0]);
  const lastSentTimeRef = useRef<number>(0);
  const currentPositionRef = useRef<number[]>([0, 0, 0]);

  // Keep position ref updated
  useEffect(() => {
    currentPositionRef.current = currentPosition;
  }, [currentPosition]);

  useEffect(() => {
    if (!myEntityId) {
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastSend = now - lastSentTimeRef.current;
      const pos = currentPositionRef.current;

      // Only send if we have an entity ID
      if (!myEntityIdRef.current) return;

      // Send if position changed and enough time has passed (100ms = 10 updates/sec)
      const positionChanged =
        pos[0] !== lastSentPositionRef.current[0] ||
        pos[1] !== lastSentPositionRef.current[1] ||
        pos[2] !== lastSentPositionRef.current[2];

      if (positionChanged && timeSinceLastSend >= 100) {
        send({
          type: 'update_position',
          position: pos,
        });
        lastSentPositionRef.current = [...pos];
        lastSentTimeRef.current = now;
      }
    }, 50); // Check every 50ms

    return () => clearInterval(interval);
  }, [myEntityId, send]);

  // Send audio output (binary protocol)
  const sendAudio = useCallback(
    (samples: number[]) => {
      if (samples.length !== 2048) {
        console.error(`Invalid sample count: ${samples.length}, expected 2048`);
        return;
      }

      // Binary frame format (matching server protocol):
      // [1 byte: MSG_TYPE_AUDIO_OUTPUT = 0x01]
      // [12 bytes: position (3 × float32)]
      // [8192 bytes: audio samples (2048 × float32)]
      // Total: 8205 bytes
      const frameSize = 1 + 12 + 8192;
      const buffer = new ArrayBuffer(frameSize);
      const uint8View = new Uint8Array(buffer);

      // Message type
      uint8View[0] = 0x01; // MSG_TYPE_AUDIO_OUTPUT

      // Position (3 float32, little-endian) - write manually to avoid alignment issues
      const posView = new DataView(buffer);
      posView.setFloat32(1, currentPosition[0], true);
      posView.setFloat32(5, currentPosition[1], true);
      posView.setFloat32(9, currentPosition[2], true);

      // Audio samples (2048 float32, little-endian) - copy to aligned offset
      const audioFloat = new Float32Array(samples);
      const audioBytes = new Uint8Array(audioFloat.buffer);
      uint8View.set(audioBytes, 13);

      sendBinary(buffer);
    },
    [sendBinary, currentPosition]
  );

  // Inject audio into another entity (admin)
  const injectAudio = useCallback(
    (entityId: string, amplitudes: number[]) => {
      send({
        type: 'inject_audio',
        entity_id: entityId,
        samples: amplitudes,
      });
    },
    [send]
  );

  // Move another entity (admin)
  const moveEntity = useCallback(
    (entityId: string, position: number[]) => {
      send({
        type: 'move_entity',
        entity_id: entityId,
        position,
      });
    },
    [send]
  );

  // Refresh entity list
  const refreshEntities = useCallback(() => {
    send({ type: 'list_entities' });
  }, [send]);

  // Disconnect and reset state
  const handleDisconnect = useCallback(() => {
    // Clear pending join and disconnect WebSocket
    pendingJoinRef.current = null;
    hasJoinedRef.current = false;
    wsDisconnect();
    setMyEntityId(null);
    setEntities([]);
  }, [wsDisconnect]);

  // Stable getter for interpolation data
  const getInterpolationData = useCallback(() => {
    return interpolationDataRef.current;
  }, []);

  return {
    entities,
    myEntityId,
    receivedAudio,
    status,
    error,
    join,
    disconnect: handleDisconnect,
    updatePosition,
    sendAudio,
    injectAudio,
    moveEntity,
    refreshEntities,
    getInterpolationData,
  };
}
